import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { IconMenu2, IconHome, IconBell, IconDots, IconLogout2 } from '@tabler/icons-react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { logoutUser, clearAuthState } from '../../redux/slices/authSlice';
import { resetChatState } from '../../redux/slices/chatSlice';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import SearchComponent from './SearchComponent';
import { useNotifications } from '../../features/notifications';
import { useFeedRefresh } from '../../features/feed';
import './Header.css';

function Header({ onMenuClick, isMobile }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { unreadCount } = useNotifications();
  const { refreshFeed } = useFeedRefresh();

  // Update dropdown position when menu opens
  useEffect(() => {
    if (menuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8, // 8px below button
        right: window.innerWidth - rect.right // Align right edge
      });
    }
  }, [menuOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
        buttonRef.current && !buttonRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleLogoutClick = () => {
    setMenuOpen(false);
    setShowLogoutDialog(true);
  };

  const handleLogout = async () => {
    try {
      // Clear all browser storage FIRST to prevent Remember Me from triggering
      localStorage.clear();
      sessionStorage.clear();

      // First dispatch logout action and wait for it to complete
      await dispatch(logoutUser()).unwrap();

      // Clear Redux auth state explicitly
      dispatch(clearAuthState());

      // FIX C3: Clear chat state on logout to prevent stale data for new users
      dispatch(resetChatState());

      // Set logout message and flag AFTER clearing storage
      sessionStorage.setItem('logoutMessage', 'You have been successfully logged out.');
      sessionStorage.setItem('justLoggedOut', 'true');
      setShowLogoutDialog(false);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      setShowLogoutDialog(false);
    }
  };

  const handleHomeClick = (e) => {
    const isOnHome = location.pathname === '/home' || location.pathname === '/';
    if (!isOnHome) return;

    e.preventDefault();
    refreshFeed();
  };

  return (
    <>
      <header className="header">
        <div className="header-container">
          <div className="header-section header-left">
            {isMobile && (
              <button className="menu-button" onClick={onMenuClick}>
                <IconMenu2 size={24} />
              </button>
            )}
            <Link to="/home" className="logo">
              Wehearthem
            </Link>
            <NavLink
              to="/home"
              className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`}
              title="Home"
              onClick={handleHomeClick}
            >
              <IconHome size={24} />
            </NavLink>
          </div>

          <div className="header-section header-center">
            <div className="search-wrapper">
              <SearchComponent />
            </div>
          </div>

          <div className="header-section header-right">
            <NavLink to="/notifications" className={({ isActive }) => `nav-icon notification-icon-wrapper ${isActive ? 'active' : ''}`} title="Notifications">
              <IconBell size={24} />
              {unreadCount > 0 && (
                <span className="notification-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </NavLink>

            <button
              ref={buttonRef}
              className="menu-dots-button"
              onClick={toggleMenu}
              aria-label="Menu"
              title="Menu"
            >
              <IconDots size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Dropdown Menu Portal */}
      {menuOpen && createPortal(
        <div
          ref={menuRef}
          className="dropdown-menu-portal"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
          }}
        >
          <button className="dropdown-item" onClick={handleLogoutClick}>
            <IconLogout2 size={20} />
            <span>Logout</span>
          </button>
        </div>,
        document.body
      )}

      <Modal
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        title="Log out of your account?"
        size="sm"
      >
        <div className="flex flex-col gap-6">
          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
            You can always log back in at any time. Your posts and connections will be saved.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowLogoutDialog(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleLogout}
              className="flex-1"
            >
              Log Out
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default Header;

